const H2_IDEAL_GAS_CONSTANT = 8.3145 // m3⋅Pa⋅K−1⋅mol−1
const H2_MOLAR_MASS = 0.002016 // kg

// p in bar and t in °C
export const convertToH2Weight = function (p: number, t: number, v: number) {
  p = p * 100000
  t = t + 273.15

  return Math.trunc((v / getMolarVolUsingSRK(p, t)) * H2_MOLAR_MASS * 100) / 100
}

// the following functions provided by Patrick J. Barrie. See https://pjb10.user.srcf.net/thermo/pure.html
export const solvecubic = function (C0: number, C1: number, C2: number) {
  let Q1 = (C2 * C1) / 6 - C0 / 2 - (C2 * C2 * C2) / 27
  let P1 = (C2 * C2) / 9 - C1 / 3
  let D = Q1 * Q1 - P1 * P1 * P1
  let temp, temp1, temp2
  let Z0, Z1, Z2
  if (D >= 0) {
    temp1 = Math.pow(Math.abs(Q1 + Math.sqrt(D)), 0.3333333333)
    temp1 = (temp1 * (Q1 + Math.sqrt(D))) / Math.abs(Q1 + Math.sqrt(D))
    temp2 = Math.pow(Math.abs(Q1 - Math.sqrt(D)), 0.3333333333)
    temp2 = (temp2 * (Q1 - Math.sqrt(D))) / Math.abs(Q1 - Math.sqrt(D))
    Z0 = temp1 + temp2 - C2 / 3
    Z1 = ""
    Z2 = ""
  } else {
    temp1 = (Q1 * Q1) / (P1 * P1 * P1)
    temp2 = Math.sqrt(1 - temp1) / Math.sqrt(temp1)
    temp2 = (temp2 * Q1) / Math.abs(Q1)
    let Phi = Math.atan(temp2)
    if (Phi < 0) {
      Phi = Phi + Math.PI
    }
    Z0 = 2 * Math.sqrt(P1) * Math.cos(Phi / 3) - C2 / 3
    Z1 = 2 * Math.sqrt(P1) * Math.cos((Phi + 2 * Math.PI) / 3) - C2 / 3
    Z2 = 2 * Math.sqrt(P1) * Math.cos((Phi + 4 * Math.PI) / 3) - C2 / 3
    if (Z0 < Z1) {
      temp = Z0
      Z0 = Z1
      Z1 = temp
    }
    if (Z1 < Z2) {
      temp = Z1
      Z1 = Z2
      Z2 = temp
    }
    if (Z0 < Z1) {
      temp = Z0
      Z0 = Z1
      Z1 = temp
    }
  }

  return Array(Z0, Z1, Z2, D)
}

// https://en.wikipedia.org/wiki/Cubic_equations_of_state#Soave_modification_of_Redlich%E2%80%93Kwong
// https://www.chegg.com/homework-help/questions-and-answers/4-generalized-soave-redlich-kwong-srk-equation-state-defined-follows-begin-aligned-p-frac--q109642368
// https://www.researchgate.net/figure/Cubic-equation-of-state_tbl1_236270198
// SRK euqation: P = R*T/(V-b)-a/V*(V+b) where V is molar volume
export const getMolarVolUsingSRK = function (P: number, T: number) {
  // converting back to bar for this algorithm
  P = P / 100000

  let R = H2_IDEAL_GAS_CONSTANT // gas constant
  let Tc = 33.3 // critical temperature of h2 (k)
  let Pc = 12.97 // critical pressure of h2 (bar)
  let Wc = -0.215 // acentric factor

  // calculate SRK "a" & "b" constants for hydrogen
  // let kappa = 0.480 + 1.574*Wc - 0.176*Wc*Wc;
  let kappa = 0.48508 + 1.55171 * Wc - 0.15613 * Wc * Wc // these set of constants seem to give better results for pressure < 300 bar
  let alpha = Math.pow(1 + kappa * (1 - Math.sqrt(T / Tc)), 2)
  let a = (0.42748 * R * R * Tc * Tc * alpha) / (Pc * 1e5)
  let b = (0.08664 * R * Tc) / (Pc * 1e5)

  // convert SRK to a cubic equation of Z, where Z = P*V/RT
  // Z is the Compressibility factor - deviation of a real gas from ideal gas behaviour
  // i.e. converting P = R*T/(V-b)-a/V*(V+b) to polynomial: Z^3 + C2*Z^2 + C1*Z + C0 = 0
  let A = (a * P * 1e5) / Math.pow(R * T, 2)
  let B = (b * P * 1e5) / (R * T)
  let C2 = -1
  let C1 = A - B - B * B
  let C0 = -A * B

  // solve the cubic equation for Z
  let Z = solvecubic(C0, C1, C2)

  // get molar volume and density (kg/m3), based on the 1st root
  return (Z[0] * R * T) / (P * 1e5)
}
